import {
  mapFromDocumentConnection,
  mapFromDocumentConnectionGrants,
  mapFromDocumentConnections,
  mapFromDocumentConnectionTestResult,
  mapFromDocumentProviders,
  mapFromProviderContainers,
  mapFromProviderItemPage,
  mapToDocumentConnection,
} from 'mappings/documentProviderMapping';
import DocumentConnection, {
  DocumentConnectionGrant,
  DocumentConnectionTestResult,
  ProviderContainer,
  ProviderItemPage,
} from 'models/documentConnection';
import { DocumentProvider } from 'models/documentProvider';
import {
  DocumentConnectionDTO,
  DocumentConnectionGrantCompleteDTO,
  DocumentConnectionGrantDTO,
  DocumentConnectionGrantStartDTO,
  DocumentConnectionTestResultDTO,
  DocumentProviderDescriptorDTO,
  ProviderContainerDTO,
  ProviderItemPageDTO,
} from 'models/dto/documentProviderDTO';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetDocumentProviders(accessToken: string): Promise<DocumentProvider[]> {
  try {
    const ar = await http.get<DocumentProviderDescriptorDTO[]>(`/documentproviders`, http.getRequestConfig(accessToken));

    return mapFromDocumentProviders(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetDocumentConnections(accessToken: string): Promise<DocumentConnection[]> {
  try {
    const ar = await http.get<DocumentConnectionDTO[]>(`/documentconnections`, http.getRequestConfig(accessToken));

    return mapFromDocumentConnections(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetDocumentConnection(
  connectionId: number,
  accessToken: string,
): Promise<DocumentConnection | undefined> {
  try {
    const ar = await http.get<DocumentConnectionDTO>(
      `/documentconnections/${connectionId}`,
      http.getRequestConfig(accessToken),
    );

    if (!ar.data) return undefined;

    return mapFromDocumentConnection(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateDocumentConnection(
  item: DocumentConnection,
  accessToken: string,
): Promise<DocumentConnection> {
  try {
    const ar = await http.post<DocumentConnectionDTO>(
      `/documentconnections`,
      mapToDocumentConnection(item),
      http.getRequestConfig(accessToken),
    );

    return mapFromDocumentConnection(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateDocumentConnection(
  item: DocumentConnection,
  accessToken: string,
): Promise<DocumentConnection> {
  try {
    const ar = await http.put<DocumentConnectionDTO>(
      `/documentconnections`,
      mapToDocumentConnection(item),
      http.getRequestConfig(accessToken),
    );

    return mapFromDocumentConnection(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteDocumentConnection(connectionId: number, accessToken: string): Promise<void> {
  try {
    await http.post(`/documentconnections/delete/${connectionId}`, {}, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiTestDocumentConnection(
  connectionId: number,
  accessToken: string,
): Promise<DocumentConnectionTestResult> {
  try {
    const ar = await http.post<DocumentConnectionTestResultDTO>(
      `/documentconnections/${connectionId}/test`,
      {},
      http.getRequestConfig(accessToken),
    );

    return mapFromDocumentConnectionTestResult(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/** The provider's authorization page for the signed-in user's own account on a user-delegated connection. */
export async function apiStartDocumentConnectionGrant(connectionId: number, accessToken: string): Promise<string> {
  try {
    const ar = await http.post<DocumentConnectionGrantStartDTO>(
      `/documentconnections/${connectionId}/grant/start`,
      {},
      http.getRequestConfig(accessToken),
    );

    return ar.data?.url ?? '';
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

/**
 * Complete the signed-in user's own grant with the code the callback page handed this window.
 * The API checks that this is the person who started the flow before it exchanges and stores.
 */
export async function apiCompleteDocumentConnectionGrant(
  connectionId: number,
  code: string,
  state: string,
  accessToken: string,
): Promise<void> {
  try {
    const body = new DocumentConnectionGrantCompleteDTO();
    body.code = code;
    body.state = state;
    await http.post(`/documentconnections/${connectionId}/grant/complete`, body, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiDeleteDocumentConnectionGrant(connectionId: number, accessToken: string): Promise<void> {
  try {
    await http.post(`/documentconnections/${connectionId}/grant/delete`, {}, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetMyDocumentConnectionGrants(accessToken: string): Promise<DocumentConnectionGrant[]> {
  try {
    const ar = await http.get<DocumentConnectionGrantDTO[]>(
      `/documentconnections/grants/mine`,
      http.getRequestConfig(accessToken),
    );

    return mapFromDocumentConnectionGrants(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetConnectionContainers(
  connectionId: number,
  parentId: string | undefined,
  accessToken: string,
): Promise<ProviderContainer[]> {
  try {
    const query = parentId ? `?parentId=${encodeURIComponent(parentId)}` : '';
    const ar = await http.get<ProviderContainerDTO[]>(
      `/documentconnections/${connectionId}/containers${query}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromProviderContainers(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetConnectionItems(
  connectionId: number,
  containerId: string,
  folderId: string | undefined,
  pageToken: string | undefined,
  accessToken: string,
): Promise<ProviderItemPage> {
  try {
    const params = new URLSearchParams();
    params.append('containerId', containerId);
    if (folderId) params.append('folderId', folderId);
    if (pageToken) params.append('pageToken', pageToken);

    const ar = await http.get<ProviderItemPageDTO>(
      `/documentconnections/${connectionId}/items?${params.toString()}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromProviderItemPage(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
